import { Navigate, Route, Routes } from "react-router-dom";

import {
  AtendidosScreen,
  ConfigScreen,
  PreciosScreen,
  SedesScreen,
} from "@/features/admin";
import { LoginScreen } from "@/features/auth";
import { CotizadorScreen } from "@/features/cotizador";
import { CutlistScreen } from "@/features/cutlist";
import { DashboardScreen } from "@/features/dashboard";
import { InventarioAdminScreen, InventarioScreen } from "@/features/inventario";
import { AppLayout, ROUTES } from "@/features/navigation";
import {
  OrderEditScreen,
  OrderNewScreen,
  OrdersCancelledScreen,
  OrdersDeliveredScreen,
  OrdersInProcessScreen,
  OrdersPendingScreen,
  OrdersSearchScreen,
} from "@/features/orders";
import {
  EditarPedidoScreen,
  HistorialPedidosScreen,
  PedidosLaminasScreen,
} from "@/features/pedidos_laminas";
import { LaserScheduleScreen, ScheduleScreen } from "@/features/scheduling";
import {
  HistoricalTalonarioScreen,
  TalonarioDetailScreen,
  TalonarioHistoryScreen,
  TalonarioMismatchesScreen,
  TalonariosScreen,
} from "@/features/talonarios";
import { AdvancedStatsScreen } from "@/features/stats";
import { RolesScreen, UsersScreen } from "@/features/team";
import { UserActivityScreen } from "@/features/user_activity";
import { RequireAuth } from "@/lib/auth";

/**
 * Tabla de rutas de la aplicación.
 *
 * `/login` es pública; el resto cuelga de `RequireAuth` (redirige a `/login` si
 * `/auth/me` no autentica) y del `AppLayout` (sidebar + cabecera con sesión).
 * La ruta de Inicio (`/`) sirve la pantalla real de Dashboard (#45), los
 * listados de Órdenes (pendientes / en proceso / entregadas / anuladas / buscar)
 * sirven las pantallas reales (#47) y la "Nueva orden" / "Editar orden" sirven el
 * formulario real (#48), Talonarios (supervisor / detalle / historial /
 * descuadres / histórico) sirve las pantallas reales (#51), Equipo (Usuarios /
 * Roles) sirve las pantallas reales (#55), Admin (Sedes / Atendidos /
 * Configuración) sirve las pantallas reales (#57), Estadísticas avanzadas sirve la
 * pantalla real con gráficos (#62) y Actividad de usuarios sirve la pantalla real
 * (#61), Horario semanal y Horario láser sirven las pantallas reales (#63) e
 * Inventario y "Activar por sede" sirven las pantallas reales (#67, multi-sede). El
 * orden y la visibilidad del menú los gobierna `features/navigation` (paridad con
 * el SSR). Las rutas se centralizan en `ROUTES`.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginScreen />} />
      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          <Route path={ROUTES.dashboard} element={<DashboardScreen />} />

          {/* Órdenes — alta y edición (#48) con formulario real */}
          <Route path={ROUTES.ordersNew} element={<OrderNewScreen />} />
          <Route path={ROUTES.ordersEdit} element={<OrderEditScreen />} />
          <Route path={ROUTES.ordersPending} element={<OrdersPendingScreen />} />
          <Route path={ROUTES.ordersInProcess} element={<OrdersInProcessScreen />} />
          <Route path={ROUTES.ordersDelivered} element={<OrdersDeliveredScreen />} />
          <Route path={ROUTES.ordersCancelled} element={<OrdersCancelledScreen />} />
          <Route path={ROUTES.ordersSearch} element={<OrdersSearchScreen />} />

          {/* Operación / Horarios (#63): pantallas reales. El SSR de schedule y
              laser_schedule se demolió en esta feature. */}
          <Route path={ROUTES.schedule} element={<ScheduleScreen />} />
          <Route path={ROUTES.laserSchedule} element={<LaserScheduleScreen />} />

          {/* Talonarios (#51): pantalla real (supervisor, detalle, historial,
              descuadres, histórico). El SSR sigue vivo hasta la #52. */}
          <Route path={ROUTES.talonarios} element={<TalonariosScreen />} />
          <Route path={ROUTES.talonarioDetail} element={<TalonarioDetailScreen />} />
          <Route path={ROUTES.talonarioHistory} element={<TalonarioHistoryScreen />} />
          <Route path={ROUTES.talonarioMismatches} element={<TalonarioMismatchesScreen />} />
          <Route path={ROUTES.talonarioHistorical} element={<HistoricalTalonarioScreen />} />

          {/* Equipo (#55): pantalla real de Usuarios. El SSR sigue vivo hasta la #56. */}
          <Route path={ROUTES.users} element={<UsersScreen />} />

          {/* Admin (#57): pantallas reales de Sedes, Atendidos y Configuración.
              El SSR sigue vivo hasta la #59. */}
          <Route path={ROUTES.sedes} element={<SedesScreen />} />
          <Route path={ROUTES.atendidos} element={<AtendidosScreen />} />
          {/* Roles (#55): pantalla real. El SSR sigue vivo hasta la #56. */}
          <Route path={ROUTES.roles} element={<RolesScreen />} />
          <Route path={ROUTES.settings} element={<ConfigScreen />} />
          {/* Precios del cotizador (#58): pantalla real. El SSR sigue vivo hasta la #59. */}
          <Route path={ROUTES.cotizadorPrecios} element={<PreciosScreen />} />

          {/* Análisis */}
          {/* Estadísticas avanzadas (#62): pantalla real con gráficos. El SSR de la página se demolió. */}
          <Route path={ROUTES.advancedStats} element={<AdvancedStatsScreen />} />
          {/* Actividad de usuarios (#61): pantalla real. El SSR de la página se demolió. */}
          <Route path={ROUTES.userActivity} element={<UserActivityScreen />} />

          {/* Inventario (#67): pantallas reales (rollos de la sede activa +
              activar por sede, multi-sede). El SSR de inventario y admin_inventario
              se demolió en esta feature. */}
          <Route path={ROUTES.inventario} element={<InventarioScreen />} />
          <Route path={ROUTES.inventarioAdmin} element={<InventarioAdminScreen />} />

          {/* Herramientas */}
          {/* Cotizador de puertas (#65): pantalla real (cálculo + ticket). El SSR de la
              página cotizador_puertas se demolió en esta feature. */}
          <Route path={ROUTES.cotizador} element={<CotizadorScreen />} />
          {/* Optimizador de cortes (#64): pantalla real. El SSR de la página cutlist
              se demolió en esta feature. */}
          <Route path={ROUTES.cutlist} element={<CutlistScreen />} />
          {/* Pedidos de láminas (#66): pantallas reales (board + editar + historial).
              El SSR de las 3 páginas se demolió en esta feature. */}
          <Route path={ROUTES.pedidosLaminas} element={<PedidosLaminasScreen />} />
          <Route path={ROUTES.pedidosLaminasHistorial} element={<HistorialPedidosScreen />} />
          <Route path={ROUTES.pedidosLaminasEdit} element={<EditarPedidoScreen />} />
        </Route>
      </Route>
      {/* Cualquier otra ruta cae a la raíz (protegida). */}
      <Route path="*" element={<Navigate to={ROUTES.dashboard} replace />} />
    </Routes>
  );
}
